import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { GoogleBigQueryLinkedService, googleBigQueryAuthTypes } from 'app/model/linkedservice/google-bigquery.linkedservice';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { Constants } from 'app/shared/shared-constants';
import { ValueAdapter } from '../value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(GoogleBigQueryLinkedService),
    disabled: __IS_FABRIC_DPE__,
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                project: '*',
                additionalProjects: '*',
                requestGoogleDriveScope: '*',
                authenticationType: '*',
                clientId: '*',
                clientSecret: 'secret',
                refreshToken: 'secret',
                email: '*',
                keyFilePath: '*',
                trustedCertPath: '*',
                useSystemTrustStore: '*',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class GoogleBigQueryLinkedServiceAdapter extends LinkedServiceAdapter<GoogleBigQueryLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => GoogleBigQueryLinkedService {
        return GoogleBigQueryLinkedService;
    }

    protected model(): IModel {
        return GoogleBigQueryLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: GoogleBigQueryLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.GoogleBigQueryLinkedService;
        properties.type = Metamodel.modelFor(GoogleBigQueryLinkedService).name;
        let clientId: string;
        let clientSecret: string | Model.SecretBase;
        let refreshToken: string | Model.SecretBase;
        let email: string;
        let keyFilePath: string;
        let trustedCertPath: string;
        let useSystemTrustStore: boolean;
        if (linkedService.authenticationType === googleBigQueryAuthTypes.UserAuthentication) {
            clientId = this.valueToResource(linkedService, GoogleBigQueryLinkedService.CLIENT_ID);
            clientSecret = parameterizablePasswordOrAKVtoSecretBase(linkedService, GoogleBigQueryLinkedService.CLIENT_SECRET, this.context);
            refreshToken = parameterizablePasswordOrAKVtoSecretBase(linkedService, GoogleBigQueryLinkedService.REFRESH_TOKEN, this.context);
        } else if (linkedService.authenticationType === googleBigQueryAuthTypes.ServiceAuthentication) {
            email = this.valueToResource(linkedService, GoogleBigQueryLinkedService.EMAIL);
            keyFilePath = this.valueToResource(linkedService, GoogleBigQueryLinkedService.KEY_FILE_PATH);
            trustedCertPath = this.valueToResource(linkedService, GoogleBigQueryLinkedService.TRUSTED_CERT_PATH);
            useSystemTrustStore = this.valueToResource(linkedService, GoogleBigQueryLinkedService.USE_SYSTEM_TRUST_STORE);
        }
        properties.typeProperties = {
            project: this.valueToResource(linkedService, GoogleBigQueryLinkedService.PROJECT_ID),
            additionalProjects: this.valueToResource(linkedService, GoogleBigQueryLinkedService.ADDITIONAL_PROJECTS) || undefined,
            requestGoogleDriveScope: this.valueToResource(linkedService, GoogleBigQueryLinkedService.REQUEST_GOOGLE_DRIVE_SCOPE),
            authenticationType: linkedService.authenticationType,
            clientId,
            clientSecret,
            refreshToken,
            email,
            keyFilePath,
            trustedCertPath,
            useSystemTrustStore
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: GoogleBigQueryLinkedService): Promise<GoogleBigQueryLinkedService> {
        const properties = linkedServiceResource.properties as Model.GoogleBigQueryLinkedService;
        const typeProperties: Model.GoogleBigQueryLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.PROJECT_ID, typeProperties.project);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.ADDITIONAL_PROJECTS, typeProperties.additionalProjects);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.REQUEST_GOOGLE_DRIVE_SCOPE, typeProperties.requestGoogleDriveScope);
        linkedService.authenticationType = typeProperties.authenticationType;
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.CLIENT_ID, typeProperties.clientId || Constants.maskedCredential);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.EMAIL, typeProperties.email);
        parameterizableSecretBaseToModel(linkedService, GoogleBigQueryLinkedService.CLIENT_SECRET, typeProperties.clientSecret, this.context, typeProperties.authenticationType === googleBigQueryAuthTypes.UserAuthentication);
        parameterizableSecretBaseToModel(linkedService, GoogleBigQueryLinkedService.REFRESH_TOKEN, typeProperties.refreshToken, this.context, typeProperties.authenticationType === googleBigQueryAuthTypes.UserAuthentication);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.KEY_FILE_PATH, typeProperties.keyFilePath);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.TRUSTED_CERT_PATH, typeProperties.trustedCertPath);
        ValueAdapter.toModel(linkedService, GoogleBigQueryLinkedService.USE_SYSTEM_TRUST_STORE, typeProperties.useSystemTrustStore);

        return linkedService;
    }
}
